import React, { useRef, useState, useEffect } from "react"

import { useCurrentSessionStore } from "../../store/useCurrentSessionStore"
import { useLocation } from "react-router-dom"
import { Button } from "primereact/button"
import { useMeshVisibilityStore } from "../../store/useMeshVisibilityStore"
import { firestoreGetShowRedFlagButtonSetting } from "../../firebase/firestore"

import { SELECT_MODE, DISSECT_MODE, AREA_MODE, GENERAL_AREAS_MODE } from "../../constants/constants"
import { useTreatmentTechniqueStore } from "../../store/useTreatmentTechniqueStore"
import { useUserStore } from "../../store/useUserStore"
import { useDictationStore } from "../../store/useDictationStore"
import { Panel } from "primereact/panel"
import { OverlayPanel } from "primereact/overlaypanel"
import { RedFlagQuestionsList, allRedFlagsAnswered, anyRedFlagYes } from "../red-flag-screening/RedFlagScreeningPanel"

const AreaButton = () => {
  const eventMode = useCurrentSessionStore((state) => state.eventMode)
  const toggleSetEventMode = useCurrentSessionStore(
    (state) => state.toggleSetEventMode
  )
  const handleToggleEvent = () => {
    const isActivating = eventMode !== AREA_MODE
    toggleSetEventMode(AREA_MODE)
    if (isActivating) {
      useDictationStore.setState({ conditionsPanelVisible: false })
    } else {
      useDictationStore.getState().showConditionsPanel()
    }
  }
  return (
    <Button
      value={AREA_MODE}
      icon='pi pi-table'
      label='Area'
      onClick={handleToggleEvent}
      className={`p-button-sm ToolbarBtn${eventMode === AREA_MODE ? " ToolbarBtn--active" : ""}`}
    />
  )
}
const SelectButton = () => {
  const eventMode = useCurrentSessionStore((state) => state.eventMode)
  const setEventMode = useCurrentSessionStore((state) => state.setEventMode)

  const handleSelect = (e) => {
    setEventMode(e.currentTarget.value)
  }
  return (
    <Button
      value={SELECT_MODE}
      label='Select'
      onClick={handleSelect}
      icon='pi pi-pencil'
      className={`p-button-sm ToolbarBtn${eventMode === SELECT_MODE ? " ToolbarBtn--active" : ""}`}
    />
  )
}

const RedFlagButton = () => {
  const op = useRef()
  const sessionId = useCurrentSessionStore((state) => state.sessionId)
  const redFlagAnswers = useCurrentSessionStore((state) => state.redFlagAnswers)
  const [showRedFlagButton, setShowRedFlagButton] = useState(false)

  useEffect(() => {
    let isMounted = true
    firestoreGetShowRedFlagButtonSetting()
      .then((setting) => {
        if (isMounted) setShowRedFlagButton(setting?.showRedFlagButton ?? false)
      })
      .catch(() => {})
    return () => {
      isMounted = false
    }
  }, [])

  if (!sessionId) return null
  if (!showRedFlagButton) return null

  const yesRecorded = anyRedFlagYes(redFlagAnswers)
  const done = allRedFlagsAnswered(redFlagAnswers)

  // Order matters: a recorded "Yes" always wins over "all answered".
  const label = yesRecorded
    ? "Red Flag — Yes recorded"
    : done
      ? "Red Flags ✓"
      : "Red Flags — Not Screened"
  const style = yesRecorded
    ? { color: "#ffffff", background: "#991B1B", border: "2px solid #ffffff", borderRadius: "999px" }
    : done
      ? { color: "#166534", background: "#ffffff", border: "1px solid #ffffff", borderRadius: "999px" }
      : { color: "#92400E", background: "#ffffff", border: "1px solid #ffffff", borderRadius: "999px" }

  return (
    <>
      <Button
        label={label}
        text
        className="p-button-sm"
        style={style}
        onClick={(e) => op.current.toggle(e)}
      />
      <OverlayPanel appendTo={document.body} ref={op}>
        <Panel header="Red Flag Screening">
          <RedFlagQuestionsList />
        </Panel>
      </OverlayPanel>
    </>
  )
}

const DissectButtons = () => {
  const op = useRef()

  const eventMode = useCurrentSessionStore((state) => state.eventMode)
  const setEventMode = useCurrentSessionStore((state) => state.setEventMode)

  const hiddenMeshes = useMeshVisibilityStore((state) => state.hiddenMeshes)
  const revealHiddenMesh = useMeshVisibilityStore(
    (state) => state.revealHiddenMesh
  )
  const revealAllHiddenMeshes = useMeshVisibilityStore(
    (state) => state.revealAllHiddenMeshes
  )
  const cb = () => {
    const num = useMeshVisibilityStore.getState().hiddenMeshes
    if (num < 1) {
      op.current.hide()
    }
  }

  const handleRevealAll = () => {
    revealAllHiddenMeshes()
    op.current.hide()
  }
  const handleSelect = (e) => {
    setEventMode(e.currentTarget.value)
  }
  const handleCallback = () => {
    cb()
  }

  return (
    <>
      <Button
        label='Dissect'
        value={DISSECT_MODE}
        icon='pi pi-minus-circle'
        onClick={handleSelect}
        className={`p-button-sm ToolbarBtn${eventMode === DISSECT_MODE ? " ToolbarBtn--active" : ""}`}
      />
      <Button
        disabled={hiddenMeshes.length < 1}
        label={`${hiddenMeshes?.length ?? 0}`}
        iconPos='right'
        icon='pi  pi-angle-down'
        className='p-button-sm ToolbarBtn'
        onClick={(e) => op.current.toggle(e)}
      />
      <OverlayPanel className='HiddenMeshOverlayPanel' ref={op}>
        <Panel className='CustomPanel ' header='Hidden'>
          <div className='flex flex-column  '>
            <div
              style={{ height: "300px", maxHeight: "340px", width: "300px" }}
              className='flex flex-column overflow-y-auto '>
              {hiddenMeshes &&
                hiddenMeshes.map((m) => (
                  <Button
                    // style={{ minHeight: "40px" }}
                    name={m.name}
                    className='my-1 p-button-sm'
                    label={m.name.replace(/_/gi, " ")}
                    key={m.id}
                    onClick={() => {
                      revealHiddenMesh(m.name)
                      handleCallback()
                    }}
                  />
                ))}
            </div>
            <div className='flex align-items-center justify-content-center p-1 mt-1'>
              <Button
                label='Reveal All'
                className='p-button-secondary p-button-sm'
                onClick={handleRevealAll}
              />
            </div>
          </div>
        </Panel>
      </OverlayPanel>
    </>
  )
}

const TechniquesButton = () => {
  const setEventMode = useCurrentSessionStore((state) => state.setEventMode)
  const techniquesModeActive = useTreatmentTechniqueStore((state) => state.techniquesModeActive)
  const setTechniquesModeActive = useTreatmentTechniqueStore((state) => state.setTechniquesModeActive)
  const clearClickedArea = useTreatmentTechniqueStore((state) => state.clearClickedArea)

  const handleClick = () => {
    if (techniquesModeActive) {
      setEventMode(SELECT_MODE)
      setTechniquesModeActive(false)
      clearClickedArea()
      useDictationStore.getState().showConditionsPanel()
    } else {
      setEventMode(GENERAL_AREAS_MODE)
      setTechniquesModeActive(true)
      useTreatmentTechniqueStore.getState().setAppendAreasMode(false)
      useDictationStore.setState({ conditionsPanelVisible: false })
      const rxTab = useUserStore.getState().userNoteSettings?.find((n) => n.abbr === "Rx")
      if (rxTab) {
        useCurrentSessionStore.getState().setCurrentNote(rxTab)
      }
    }
  }

  return (
    <Button
      icon='pi pi-list'
      label='Techniques'
      onClick={handleClick}
      className={`p-button-sm ToolbarBtn${techniquesModeActive ? " ToolbarBtn--active" : ""}`}
    />
  )
}

const SymptomsButton = () => {
  const isOpen = useDictationStore((s) => s.isOpen)
  const conditions = useDictationStore((s) => s.conditions)
  const symptomsPanelVisible = useDictationStore((s) => s.symptomsPanelVisible)
  const toggleSymptomsPanel = useDictationStore((s) => s.toggleSymptomsPanel)

  if (!isOpen || !conditions || conditions.length === 0) return null

  return (
    <Button
      label='Symptoms'
      icon='pi pi-question-circle'
      className={`p-button-sm ToolbarBtn${symptomsPanelVisible ? " ToolbarBtn--active" : ""}`}
      badge={String(conditions.length)}
      badgeClassName='p-badge-warning'
      onClick={toggleSymptomsPanel}
    />
  )
}

const ToolButtons = () => {
  return (
    <>
      <SymptomsButton />
      <RedFlagButton />
      <SelectButton />
      <div className='p-buttonset flex align-items-center'>
        <DissectButtons />
      </div>
      <AreaButton />
      <TechniquesButton />
    </>
  )
}

export const TreatmentToolbar = () => {
  let location = useLocation()

  if (location?.pathname !== "/user/treatment") {
    return null
  }

  return (
    <div className='border-round  flex  align-items-center p-1 gap-1 TreatmentToolbar'>
      <ToolButtons />
    </div>
  )
}
